import React from 'react';
import PropTypes from 'prop-types';

import { reduxForm, Field, SubmissionError } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import TextField from 'components/fields/TextField';
import SwitchField from 'components/fields/SwitchField';
import Section, { SectionHeader, SectionTitle, SectionBody } from 'components/common/Section';
import FormError from 'components/common/FormError';
import Button from 'components/common/Button';

import * as validate from 'utils/validate';
import objectDiff from 'utils/objectDiff';
import groupAccessPermissions from 'utils/groupAccessPermissions';

import {
  createRole,
  updateRole,
  addRoleResource,
  removeRoleResource,
} from 'redux/modules/roles';

import RolePermissionsCreateEditFormSection from './RolePermissionsCreateEditFormSection';
import RolePermissionsCreateEditModel from './RolePermissionsCreateEditModel';

const styles = theme => ({
  form: {
    width: 730,
    margin: '10px auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  grid: {
    marginBottom: 8,
  },
  button: {
    marginLeft: 20,
  },
  sectionBody: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 24,
    },
  },
});

function onSubmit(values, dispatch, props) {
  const { Access, ...otherValues } = values;
  delete otherValues['Full Access'];
  if (props.initialValues.ID) {
    if (props.pristine) {
      return values;
    }
    const diffValues = objectDiff(values, props.initialValues);
    const addedAccess = Object.keys(values.Access)
      .filter(item =>
        values.Access[item] && !props.initialValues.Access[item])
      .map(item => Number(item.replace('ID-', '')));
    const removedAccess = Object.keys(props.initialValues.Access)
      .filter(item =>
        props.initialValues.Access[item] && !values.Access[item])
      .map(item => Number(item.replace('ID-', '')));
    delete diffValues.Access;
    delete diffValues['Full Access'];
    return new Promise((resolve, reject) => {
      if (Object.keys(diffValues).length > 0) {
        dispatch(updateRole({
          values: {
            ID: props.initialValues.ID,
            ...diffValues,
          },
          resolve,
          reject,
        }));
      } else {
        resolve();
      }
    })
      .then(() => new Promise((resolve, reject) => {
        if (addedAccess.length > 0) {
          dispatch(addRoleResource({
            values: {
              ID: props.initialValues.ID,
              'Access ID': addedAccess,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (removedAccess.length > 0) {
          dispatch(removeRoleResource({
            values: {
              ID: props.initialValues.ID,
              'Access ID': removedAccess,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(res => res,
        (err) => {
          throw new SubmissionError({ _error: err.error });
        });
  }
  return new Promise((resolve, reject) => {
    dispatch(createRole({
      values: {
        ...otherValues,
        'Access ID': Object.keys(Access)
          .filter(item => Access[item])
          .map(item => Number(item.replace('ID-', ''))),
      },
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      throw new SubmissionError({ _error: err.error });
    });
}

function onChange(values, dispatch, props, prevValues) {
  if (values['Full Access'] && !prevValues['Full Access']) {
    props.accessPermissions.forEach((item) => {
      dispatch(props.change(`Access.ID-${item.ID}`, true));
    });
  } else if (!values['Full Access'] && prevValues['Full Access']) {
    const uncheckedAccessPermissions = props.accessPermissions.filter(item =>
      !values.Access[`ID-${item.ID}`]);
    if (uncheckedAccessPermissions.length === 0) {
      props.accessPermissions.forEach((item) => {
        dispatch(props.change(`Access.ID-${item.ID}`, false));
      });
    }
  } else if (values['Full Access'] && prevValues['Full Access']) {
    const uncheckedAccessPermissions = props.accessPermissions.filter(item =>
      !values.Access[`ID-${item.ID}`] && prevValues.Access[`ID-${item.ID}`]);
    if (uncheckedAccessPermissions.length > 0) {
      dispatch(props.change('Full Access', false));
    }
  } else if (!values['Full Access'] && !prevValues['Full Access']) {
    const checkedAccessPermissions = props.accessPermissions.filter(item =>
      values.Access[`ID-${item.ID}`]);
    if (checkedAccessPermissions.length === props.accessPermissions.length) {
      dispatch(props.change('Full Access', true));
    }
  }
}

const RolePermissionsCreateEditForm = ({
  handleSubmit,
  accessPermissions,
  submitting,
  submit,
  initialValues,
  error,
  variant,
  classes,
}) => {
  const groupedAccessPermissions = groupAccessPermissions(accessPermissions);
  return (
    <Section>
      <SectionHeader fixed top={110}>
        <SectionTitle>
          {initialValues.ID ? 'Edit Role' : 'Create Role'}
        </SectionTitle>
        <div>
          <Button
            className={classes.button}
            variant="raised"
            href="/settings/users"
          >
            Cancel
          </Button>
          <Button
            className={classes.button}
            variant="raised"
            color="primary"
            disabled={submitting}
            onClick={submit}
          >
            Done
          </Button>
        </div>
      </SectionHeader>
      <SectionBody className={classes.sectionBody}>
        <form
          className={classes.form}
          noValidate
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          {error &&
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid
                item
                xs={12}
              >
                <FormError>
                  {error}
                </FormError>
              </Grid>
            </Grid>
          }
          <Grid
            className={classes.grid}
            container
            alignItems="center"
            spacing={24}
          >
            <Grid
              item
              xs={12}
              md={4}
            >
              <Field
                id="Name"
                name="Name"
                label="Role Name"
                component={TextField}
                validate={[validate.required]}
                fullWidth
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={8}
            >
              <Field
                id="Description"
                name="Description"
                label="Description"
                component={TextField}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid
            className={classes.grid}
            container
            alignItems="center"
            spacing={24}
            wrap="nowrap"
          >
            <Grid
              item
              xs={12}
            >
              <Typography>
                Customize the permissions below to tailor the way
                team members with this role use our system
              </Typography>
            </Grid>
          </Grid>
          <Grid
            className={classes.grid}
            container
            alignItems="center"
            justify="space-between"
            spacing={16}
            wrap="nowrap"
          >
            <Grid item xs={12}>
              <Field
                id="Full Access"
                name="Full Access"
                label="Full Access"
                subLabel="This enables all actions and permissions in every part of the system"
                component={SwitchField}
              />
            </Grid>
            {variant === 'create' &&
              <Grid item>
                <RolePermissionsCreateEditModel />
              </Grid>
            }
          </Grid>
          {groupedAccessPermissions.map(item => (
            <RolePermissionsCreateEditFormSection
              key={item.Group}
              {...item}
            />
          ))}
        </form>
      </SectionBody>
    </Section>
  );
};

RolePermissionsCreateEditForm.propTypes = {
  accessPermissions: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['create', 'edit', 'copy']),
  initialValues: PropTypes.shape({
    ID: PropTypes.number,
  }).isRequired,
  error: PropTypes.string,
  submit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
  }).isRequired,
};

RolePermissionsCreateEditForm.defaultProps = {
  error: null,
  variant: 'create',
};

export default reduxForm({
  onChange,
  onSubmit,
  enableReinitialize: true,
})(withStyles(styles)(RolePermissionsCreateEditForm));



// WEBPACK FOOTER //
// ./src/components/settings/RolePermissionsCreateEditForm/RolePermissionsCreateEditForm.js